// Phase 32 Plan 32-03 Task 2 — RiverReveal cinematic (RUNE-34).
//
// NEW component — same shape as FlopReveal modulo:
//   - displayWord 'THE RIVER' via reveal.t11.title
//   - subtitle 'The court is full.' via reveal.t11.subtitle
//   - announce key a11y.announce.reveal_t11_river
//   - theme-accent flash hue (`--color-cta-primary` per UI-SPEC §4 — for
//     v1.2 the River uses the same Sun-Yellow placeholder; Plan 32-05 may
//     swap to a theme-accent variable if VD-review requests it).
//
// T-32-07 mitigation: this component shares useRevealFSM with FlopReveal,
// so the timing ladder and reduced-motion fork cannot drift between the
// two reveals. The DOM body intentionally MIRRORS FlopReveal's structure
// — the diff is copy + data-reveal attribute only.

import { type JSX, type KeyboardEvent, useEffect } from 'react'
import { useIntl } from 'react-intl'
import { playSfx } from '../../game/audio/useAudio'
import { REVEAL_FALLBACK_STRINGS } from '../../game/reveals/timing'
import { pulseBoardReanchor } from '../../stores/board-reanchor'
import { $cascadeFrontier } from '../../stores/cascade-frontier'
import { useAnnounce } from '../components/useAnnounce'
import { useRevealFSM } from './useRevealFSM'

export interface RiverRevealProps {
  /** Cell indices that comprise the T11 growth wave (cells 48..59). */
  readonly newCellIndices: ReadonlyArray<number>
  readonly onComplete: () => void
}

export function RiverReveal({
  newCellIndices: _newCellIndices,
  onComplete,
}: RiverRevealProps): JSX.Element {
  const intl = useIntl()
  const announce = useAnnounce()
  const fsm = useRevealFSM({ onComplete })

  useEffect(() => {
    announce(
      intl.formatMessage({ id: 'a11y.announce.reveal_t11_river' }),
      'polite',
    )
  }, [announce, intl])

  // THE RIVER cue: drumroll build → deep organ chord on settle. Plays once
  // on mount regardless of reduced motion (audio is gated on mute only).
  // Phase 2 (court-opens-at-the-center): also fire the 'grow-impact' grow-thud
  // here on MOUNT — mount runs in BOTH motion modes (reduced + full), so the
  // thud is reduced-motion-safe. Deliberately NOT hooked to boardGrowReleased
  // (which never pulses under reduced motion) so the audio lands every time.
  useEffect(() => {
    playSfx('river')
    playSfx('grow-impact')
  }, [])

  // Mirror the FSM's cascade frontier into the global atom so
  // LetterCubeSprite (Plan 32-05) can gate spawn-in for cells 48..59.
  useEffect(() => {
    $cascadeFrontier.set(fsm.cascadeFrontier)
  }, [fsm.cascadeFrontier])
  // Mirror the FSM's floor-drop release into $boardReanchor so existing
  // LetterCubeSprite cubes start their old→new anchor glide at the
  // floor-drop beat (~t=900ms), overlapping the new-cell cascade (Task 5).
  useEffect(() => {
    if (fsm.boardGrowReleased) pulseBoardReanchor()
  }, [fsm.boardGrowReleased])
  useEffect(() => {
    return () => {
      // Reset cascade frontier so a subsequent reveal starts its 0..12 count
      // from a clean baseline. Do NOT reset $boardReanchor here — the epoch
      // is monotonic across the full match and must not reset between reveals.
      $cascadeFrontier.set(0)
    }
  }, [])

  const title = intl.formatMessage({
    id: 'reveal.t11.title',
    defaultMessage: REVEAL_FALLBACK_STRINGS.t11.title,
  })
  const subtitle = intl.formatMessage({
    id: 'reveal.t11.subtitle',
    defaultMessage: REVEAL_FALLBACK_STRINGS.t11.subtitle,
  })

  const handleTap = (): void => {
    if (fsm.skipEnabled) fsm.dismiss()
  }
  const handleKey = (e: KeyboardEvent<HTMLButtonElement>): void => {
    if (fsm.skipEnabled && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault()
      fsm.dismiss()
    }
  }

  return (
    // Native <button> for tap-to-skip — same a11y shape as FlopReveal.
    <button
      type="button"
      disabled={!fsm.skipEnabled}
      aria-label={intl.formatMessage({
        id: 'a11y.announce.reveal_t11_river',
        defaultMessage:
          'The river reveal. Twelve new runes appear on the field.',
      })}
      data-reveal="river"
      data-phase={fsm.phase}
      data-skip-enabled={fsm.skipEnabled ? 'true' : 'false'}
      data-reduced-motion={fsm.reducedMotion ? 'true' : 'false'}
      data-cascade-frontier={fsm.cascadeFrontier}
      onClick={handleTap}
      onKeyDown={handleKey}
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 60,
        pointerEvents: fsm.skipEnabled ? 'auto' : 'none',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'transparent',
        border: 'none',
        padding: 0,
        margin: 0,
        cursor: fsm.skipEnabled ? 'pointer' : 'default',
        font: 'inherit',
        color: 'inherit',
        textAlign: 'inherit',
      }}
    >
      {/* Top + bottom scrim — Inkwell-tinted gradients (matches FlopReveal). */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: '0 0 auto 0',
          height: 100,
          background:
            'linear-gradient(180deg, var(--color-inkwell), transparent)',
          pointerEvents: 'none',
        }}
      />
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 'auto 0 0 0',
          height: 280,
          background:
            'linear-gradient(0deg, var(--color-inkwell), transparent)',
          pointerEvents: 'none',
        }}
      />
      <h1
        className="display-word gradient"
        style={{
          position: 'relative',
          textAlign: 'center',
          fontFamily: 'var(--font-family-display)',
          fontWeight: 700,
          fontSize: 'var(--font-size-52)',
          letterSpacing: '-0.02em',
          margin: 0,
          color: 'var(--color-cta-primary)',
        }}
      >
        {title}
      </h1>
      <span
        style={{
          position: 'relative',
          textAlign: 'center',
          fontFamily: 'var(--font-family-body)',
          fontWeight: 600,
          fontSize: 'var(--font-size-14)',
          fontStyle: 'italic',
          color: 'var(--color-text-secondary)',
          marginTop: 'var(--space-4)',
          letterSpacing: '0.04em',
        }}
      >
        {subtitle}
      </span>
    </button>
  )
}
